/* eslint-disable max-lines */

import React, { useContext, useState, useEffect } from "react";
import _ from "lodash";
import AppContext from "../../AppContext";
import {
  SpacingBox,
  Input,
  PrimaryButton,
  DatePicker,
  Dropdown,
  FlexBox,
  PercentInputWithLabel,
  RedParagraph,
  CustomerList,
  Textarea,
} from "../../Components";
import { getTranslateByScope } from "../../i18n";
import {
  getOrderForId,
  getCustomerForOrder,
  getAvailableDeliveryPeriods,
  getDeliveryOptions,
} from "../../selectors";
import { getIdRouteParamFromProps, getRouteState } from "../../utils/routes";
import {
  mapDeliveryOptions,
  mapDeliveryPeriodsForLetters,
} from "../../utils/products";
import { routes } from "../../routes";
import AddressDropdown from "../AddressDropdown";
import SelectCustomerDropdown from "../SelectCustomerDropdown";
import { getTotalDiscount } from "../../utils/discount";
import LinkToSalesforce from "../../Components/LinkToSalesforce";
import { getSeasons } from "../../selectors";

function CustomerName(props) {
  const account = props.account;
  const name = account.Name;

  const isBlocked = account.Blocked__c > 0;
  const blockedText = account.Customer_No_blocked_Text__c;

  return (
    <><LinkToSalesforce obj={account} /> {name} {isBlocked && <span style={{ fontSize: '0.7rem', fontStyle: 'italic' }}>{blockedText}</span>}</>
  )
}

const OrderHeader = (props) => {
  const context = useContext(AppContext);
  const account = props.account;
  const order = props.order;
  const seasonCode = _.get(order, "doc.season");
  const seasons = getSeasons(context);
  const season = _.find(seasons, (x) => x.season_code === seasonCode);

  if (account === undefined || account === null) {
    return <div>Order Header</div>;
  }

  let seasonStr = seasonCode;
  if (season !== undefined) {
    seasonStr = `${season.name} - (${seasonCode})`;
  }

  return (
    <div>
      <h3>
        <b>{translate("title")}</b>
      </h3>
      <b>{translate("account")}</b>: <CustomerName account={account} />
      <br />
      <b>
        <LinkToContactOverview accountId={account.Id} />
      </b>
      <br />
      <b>{translate("season")}</b>: {seasonStr}
    </div>
  );
};

function LinkToContactOverview(props) {
  const accountId = props.accountId;
  const link = `https://schoeffel.lightning.force.com/lightning/r/Account/${accountId}/related/Contacts/view`;
  return (
    <a href={link} target="_blank" rel="noopener noreferrer">
      {translate("contact_overview")}
    </a>
  );
}

const orderTypes = [
  { label: "PREORDER", value: "PREORDER" },
  { label: "BLOCK", value: "BLOCK" },
  { label: "TEAMWEAR", value: "TEAMWEAR" },
];

const translate = getTranslateByScope("app.orderUpdate");

const FieldSpacing = ({ children }) => (
  <SpacingBox marginTop="lg">{children}</SpacingBox>
);

export const OrderUpdate = (props) => {
  const [customer, setCustomer] = useState(null);
  const [orderDate, setOrderDate] = useState(new Date());
  const [minDeliveryDate, setMinDeliveryDate] = useState();
  const [discount1, setDiscount1] = useState("0");
  const [discount2, setDiscount2] = useState("0");
  const [discount3, setDiscount3] = useState("0");
  const [discount, setDiscount] = useState("0");
  const [orderNumber, setOrderNumber] = useState();
  const [notes, setNotes] = useState();
  const [orderType, setOrderType] = useState();
  const [deliveryAddress, setDeliveryAddress] = useState();
  const [defaultDeliveryDate, setDefaultDeliveryDate] = useState();
  const [errorMessage, setErrorMessage] = useState();

  const id = getIdRouteParamFromProps(props);
  const { actions, ...context } = useContext(AppContext);

  const order = getOrderForId(context)(id);
  const initialCustomer = getCustomerForOrder(context, order);

  useEffect(() => {
    setCustomer(initialCustomer);
  }, [initialCustomer]);

  useEffect(() => {
    const totalDiscount = getTotalDiscount(discount1, discount2, discount3);
    setDiscount(totalDiscount);
  }, [discount1, discount2, discount3]);

  useEffect(() => {
    if (!order) return;
    setOrderDate(order.doc.orderDate || new Date());
    setMinDeliveryDate(order.doc.minDeliveryDate);
    if (order.doc.discount1) {
      setDiscount1(order.doc.discount1);
    }
    if (order.doc.discount2) {
      setDiscount2(order.doc.discount2);
    }
    if (order.doc.discount3) {
      setDiscount3(order.doc.discount3);
    }
    if (order.doc.discount) {
      setDiscount(order.doc.discount);
    }
    setOrderType(order.doc.orderType);
    setOrderNumber(order.doc.orderNumber);
    setNotes(order.doc.notes);
    setDeliveryAddress(order.doc.deliveryAddress);
    setDefaultDeliveryDate(order.doc.defaultDeliveryDate);
  }, [order]);

  const availablePeriods = getAvailableDeliveryPeriods(context);
  const deliveryOptions = getDeliveryOptions(context);

  const handleUpdate = () => {
    let message = [];
    if (!customer) {
      message = [...message, translate("errorMessages.missingCustomer")];
    }

    if (!orderType) {
      message = [...message, translate("errorMessages.missingOrderType")];
    }

    if (!deliveryAddress) {
      message = [...message, translate("errorMessages.missingDeliveryAddress")];
    }

    if (message.length > 0) {
      setErrorMessage(message);
      return;
    }

    actions.updateOrder(id, {
      ...order.doc,
      customerId: customer.Id || customer.id || null,
      orderDate,
      minDeliveryDate,
      discount,
      discount1,
      discount2,
      discount3,
      orderNumber,
      notes,
      orderType,
      defaultDeliveryDate,
      deliveryAddress,
    });

    setTimeout(() => {
      const routeOnSubmit = getRouteState(props, "routeOnSubmit");
      props.history.push(routeOnSubmit || routes.routeOverview);
    }, 1000);

    actions.setToast({
      title: translate("toast.title"),
      text: translate("toast.description"),
    });
  };

  const updateCustomerAndRemovePreviousDeliveryDate = (newCustomer) => {
    setCustomer(newCustomer);
    setDeliveryAddress(null);
    setErrorMessage([translate("errorMessages.customerUpdateDeliveryAddress")]);
  };

  const showCustomerList = true;
  const seasonCode = _.get(order, "doc.season");
  const deliverySelectOptions = mapDeliveryOptions(seasonCode, deliveryOptions);

  return (
    <div className="margin-10p">
      <FlexBox flexDirection="column" marginLeft="md" marginRight="md">
        <FieldSpacing>
          <SpacingBox marginBottom="lg">
            <OrderHeader account={customer} order={order} />
          </SpacingBox>
          {showCustomerList && (
            <SpacingBox>
              {customer && <CustomerList customerId={customer.Id} />}
            </SpacingBox>
          )}
          <b>{translate("account")}</b>
          <SelectCustomerDropdown
            value={customer}
            selectCustomer={updateCustomerAndRemovePreviousDeliveryDate}
          />
          <br />
          <b>{translate("orderType")}</b>
          <Dropdown
            onChange={setOrderType}
            value={orderType}
            options={orderTypes}
          />
          <br />

          <b>{translate("discount")}</b>
          <FlexBox flexGrow={1} flexDirection="horizontal">
            <div style={{ flex: 1 }}>
              <PercentInputWithLabel
                onChange={setDiscount1}
                value={discount1}
                label={translate("discount1")}
              />
            </div>
            <div style={{ flex: 1 }}>
              <PercentInputWithLabel
                onChange={setDiscount2}
                value={discount2}
                label={translate("discount2")}
              />
            </div>
            <div style={{ flex: 1 }}>
              <PercentInputWithLabel
                onChange={setDiscount3}
                value={discount3}
                label={translate("discount3")}
              />
            </div>
            <div>
              <PercentInputWithLabel
                disabled={true}
                value={discount}
                label={translate("discount")}
              />
            </div>
          </FlexBox>
        </FieldSpacing>
        <br />

        <b>{translate("orderDate")}</b>
        <DatePicker value={orderDate} onConfirm={setOrderDate} />
        <br />

        <b>{translate("minDeliveryDate")}</b>
        <Dropdown
          value={deliverySelectOptions.find((o) => o.value === minDeliveryDate)}
          options={deliverySelectOptions}
          onChange={(o) => setMinDeliveryDate(o.value)}
        />
        <br />

        <b>{translate("defaultDeliveryDate")}</b>
        <Dropdown
          value={defaultDeliveryDate}
          options={mapDeliveryPeriodsForLetters(availablePeriods)}
          onChange={setDefaultDeliveryDate}
        />
        <br />

        <b>{translate("notes")}</b>
        <Textarea onChange={setNotes} paddingBelow="50px">
          {notes}
        </Textarea>
        <br />

        <b>{translate("orderNumber")}</b>
        <Input onChange={setOrderNumber} value={orderNumber} maxLength={34} />
        <br />

        <AddressDropdown
          key={customer}
          customer={customer}
          onChange={setDeliveryAddress}
          address={deliveryAddress}
          translations={{
            title: translate("deliveryAddress"),
            editTitle: translate("editDeliveryAddress"),
          }}
        />

        {_.uniq(errorMessage).map((e) => (
          <RedParagraph>{e}</RedParagraph>
        ))}

        <FieldSpacing>
          <PrimaryButton
            onClick={handleUpdate}
            text={translate("addOrderDataButton")}
          />
        </FieldSpacing>
      </FlexBox>
    </div>
  );
};

export default OrderUpdate;
